import { jwtDecode } from "jwt-decode";
import type { TEnv } from "./context/environment";
export type { TEnv } from "./context/environment";

const env =
  typeof window === "undefined" ? (typeof process === "undefined" ? { DEV: "true" } : process.env) : import.meta.env;

export function getArtisHeaders(token?: string): Record<string, string> {
  const e = env as Record<string, string | undefined>;
  const headers: Record<string, string> = {
    "X-Artis-Version": e.VITE_GIT_TAG || e.VITE_GIT_SHA || "",
    "X-Artis-Branch": e.VITE_GIT_BRANCH || "",
  };
  if (token) {
    try {
      const claims = jwtDecode<JWT>(token);
      if (claims.sub) headers["X-User-Id"] = claims.sub;
      if (claims.org_id != null) headers["X-Artis-Org"] = String(claims.org_id);
    } catch {}
  }
  return headers;
}

export function questDbUrl() {
  if (typeof env.VITE_QUEST_URL === "string") {
    return env.VITE_QUEST_URL;
  }
  throw new Error("VITE_QUEST_URL is not set");
}

export const isDev = env.DEV === "true" || env.MODE === "development";

export const isDebugMode =
  typeof window !== "undefined" &&
  (isDev || window.location.hostname === "localhost" || window.location.hostname.includes("netlify.app"));

export const isLocalhost = typeof window !== "undefined" && window.location.hostname === "localhost";

export const isNoneProduction = typeof window !== "undefined" && !window.location.hostname.includes("app.artis.works");

export const vapidKeys = {
  publicKey: env.VITE_VAPID_PUBLIC_KEY,
  privateKey: env.VITE_VAPID_PRIVATE_KEY,
};

// Global Definitions
export const SENTRY_DSN =
  "https://bd593a3a3051c2ccb39b3f1471819177@o4505007010414592.ingest.sentry.io/4505691086520320";

export const AG_KEY =
  "Using_this_{AG_Grid}_Enterprise_key_{AG-130570}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Artis_Works_Ltd}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Artis_Folio}_only_for_{4}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Artis_Folio}_need_to_be_licensed___{Artis_Folio}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{26_April_2027}____[v3]_[01]_MTgwODY5NDAwMDAwMA==20664e96b306c4b77d5506efd62e5065";

export const GIT_SHA = env.GIT_SHA || "NONE";
export const GIT_TAG = env.GIT_TAG || "NONE";
export const RELEASE_TIME = env.RELEASE_TIME || "NONE";

export const AUTH0_CLIENT_ID = env.VITE_AUTH0_CLIENT_ID || "e7bR2BuJBNFRacmoWWEnWsd2RJnusOeC";
export const AUTH0_DOMAIN = env.VITE_AUTH0_DOMAIN || "https://auth.dev.artis.works";

// Sanity

export const SANITY_PROJECT_ID = "fajb23hf";
export const SANITY_DATASET = "production";
export const SANITY_URL = `https://${SANITY_PROJECT_ID}.api.sanity.io/v2023-08-01/graphql/${SANITY_DATASET}/default`;

// DEV Environment
export const DEV_HASURA_URL = "https://multi-hasura.dev.artis.works/v1/graphql";
export const DEV_AUTH0_GLOBAL_ORG_ID = "org_3R60rCG9HWYwPpC0";
export const DEV_AUTH0_DOMAIN = "artisworksdev.eu.auth0.com";
export const DEV_AUTH0_CLIENT_ID = "hS9e5ZQX2wkVhbFYDuk9OohifbDaMMVp";

// UAT Environment
export const UAT_HASURA_URL = "https://multi-hasura.staging.artis.works/v1/graphql";
export const UAT_AUTH0_GLOBAL_ORG_ID = "org_uajc8Wr0rLFlqXz8";
export const UAT_AUTH0_DOMAIN = "artisworks.eu.auth0.com";
export const UAT_AUTH0_CLIENT_ID = "gmIEAJ6HGMnBsucqQ4D87Z6dT62hyzsm";

// PROD Environment
export const GLOBAL_PROD_HASURA_URL = "https://multi-hasura.artis.works/v1/graphql";
export const AUTH0_GLOBAL_ORG_ID = "org_kUN3WbpsHzp0hbX6";
export const GLOBAL_PROD_AUTH0_DOMAIN = "artisfoliostaging.eu.auth0.com";
export const GLOBAL_PROD_AUTH0_CLIENT_ID = "tEYYvbcCrJBTjzQKtouAp0lyNSrliPhZ";

// TPICAP Environment
export const TPICAP_PROD_HASURA_URL = "https://prod-folio-tpicap.hasura.app/v1/graphql";
export const AUTH0_TPICAP_ORG_ID = "org_XYwWBaz6vrPdxjeW";
export const TPICAP_PROD_AUTH0_DOMAIN = "artisfoliostaging.eu.auth0.com";
export const TPICAP_PROD_AUTH0_CLIENT_ID = "tEYYvbcCrJBTjzQKtouAp0lyNSrliPhZ";

export const ORG_TO_HASURA_URL = {
  [DEV_AUTH0_GLOBAL_ORG_ID]: DEV_HASURA_URL,
  [UAT_AUTH0_GLOBAL_ORG_ID]: UAT_HASURA_URL,
  [AUTH0_GLOBAL_ORG_ID]: GLOBAL_PROD_HASURA_URL,
  [AUTH0_TPICAP_ORG_ID]: TPICAP_PROD_HASURA_URL,
} as const;

// A Hasura running on a developer's machine serves every organisation, so it stands in for all of
// the environments below. Set HASURA_URL in the env or apps/webapp/.env (vite.config maps it in). The DEV guard keeps
// this out of deployed builds, where HASURA_URL may well be set and must not be allowed to collapse
// the per-organisation routing onto a single endpoint.
const localHasuraUrl = () => {
  const e = env as Record<string, string | undefined>;
  return e.DEV ? e.VITE_HASURA_URL : undefined;
};

export function getHasuraUrl(env: TEnv) {
  const local = localHasuraUrl();
  if (local) return local;

  const map = {
    dev: DEV_HASURA_URL,
    uat: UAT_HASURA_URL,
    "global-Prod": GLOBAL_PROD_HASURA_URL,
    "TPICAP-Prod": TPICAP_PROD_HASURA_URL,
  } as const;
  const url = map[env];
  if (url) {
    return url;
  }
  throw new Error(`No url found for env: ${env}`);
}

export function getHasuraUrlByOrg(orgId: keyof typeof ORG_TO_HASURA_URL) {
  const url = ORG_TO_HASURA_URL[orgId];

  if (!url) throw new Error(`No Hasura URL found for Org ID: ${orgId}`);

  return url;
}

export function orgIdToEnv(orgId: TOrgId) {
  const map = {
    [DEV_AUTH0_GLOBAL_ORG_ID]: "dev",
    [UAT_AUTH0_GLOBAL_ORG_ID]: "uat",
    [AUTH0_GLOBAL_ORG_ID]: "global-Prod",
    [AUTH0_TPICAP_ORG_ID]: "TPICAP-Prod",
  } as const;
  const env = map[orgId];
  if (env) {
    return env;
  }
  throw new Error(`No env found for orgId: ${orgId}`);
}

export function getAuth0Config(env: TEnv) {
  const configs = {
    dev: {
      domain: DEV_AUTH0_DOMAIN,
      clientId: DEV_AUTH0_CLIENT_ID,
      connection: "Username-Password-Authentication",
    },
    uat: {
      domain: UAT_AUTH0_DOMAIN,
      clientId: UAT_AUTH0_CLIENT_ID,
      connection: "Username-Password-Authentication",
    },
    "global-Prod": {
      domain: GLOBAL_PROD_AUTH0_DOMAIN,
      clientId: GLOBAL_PROD_AUTH0_CLIENT_ID,
      connection: "Username-Password-Authentication",
    },
    "TPICAP-Prod": {
      domain: TPICAP_PROD_AUTH0_DOMAIN,
      clientId: TPICAP_PROD_AUTH0_CLIENT_ID,
      connection: "Username-Password-Authentication",
    },
  } as const;
  const config = configs[env];
  if (config) return config;
  throw new Error(`No Auth0 config found for env: ${env}`);
}

// Styles
// Note that changing anything in this file will cause a full reload of the app
// due to the way vite works so better not to put anything in here that changes
// often unless its really shared all over the app.
export const headerHeight = 55;

export type ResObject<E = unknown> = {
  id: string;
  error: E | null;
};

export type TServerResponse<E = unknown> = ResObject<E>[] | undefined | null;

export type HasuraClaims = {
  "x-hasura-allowed-roles": string[];
  "x-hasura-default-role": string;
  "x-hasura-org-id": string;
  "x-hasura-user-email": string;
  "x-hasura-user-id": string;
};

type TOrgId =
  | typeof DEV_AUTH0_GLOBAL_ORG_ID
  | typeof UAT_AUTH0_GLOBAL_ORG_ID
  | typeof AUTH0_GLOBAL_ORG_ID
  | typeof AUTH0_TPICAP_ORG_ID;

export type JWT = {
  "https://hasura.io/jwt/claims": HasuraClaims | null;
  iss: string;
  sub: string;
  aud: string[];
  iat: number;
  exp: number;
  azp: string;
  scope: string;
  org_id: TOrgId;
  sid: string;
};
